import ContentNormal from "components/elements/ContentNormal"
import { AppContext } from "contexts/AppContext"
import React, { useContext, useEffect, useState } from "react"
import { callAPI } from "utils/API"
import "./Help.css"
import { useNavigate } from "react-router-dom";
import { Accordion, AccordionDetails, AccordionSummary, Box, accordionSummaryClasses, styled, useMediaQuery } from "@mui/material"
import SectionWrapper from "components/layouts/SectionWrapper"
import { ArrowForwardIosSharp } from "@mui/icons-material"
import Headline3 from "components/elements/Headline3"
import CircularIndeterminate from "components/blocks/progressBar/progressBar"
import Headline5 from "components/elements/Headline5"
import BannerSection from "components/blocks/bannerSection/bannerSection"
import ButtonMedium4 from "components/elements/ButtonMedium4"

const UpgraderAccordion = styled((props) => (
    <Accordion disableGutters elevation={0} square {...props} />
))(() => ({
    borderTop: '1px solid #efefef',
    '&:last-child': {
        borderBottom: '1px solid #efefef',
    },
    '&::before': {
        display: 'none',
    },
}));

const UpgraderAccordionSummary = styled((props) => (
    <AccordionSummary
        expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}
        {...props}
    />
))(() => ({
    paddingLeft: 0,
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
        transform: 'rotate(90deg)',
    },
}));

const UpgraderAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    paddingLeft: 0,
}));

const Help = () => {
    const navigate = useNavigate()
    const isMobile = useMediaQuery('(max-width:900px)'); 
    const { accessibleBlogs, accessibleShops, langCode, brandData, styleGuide } = useContext(AppContext)
    const [faqCategories, setFaqCategories] = useState([])
    const [expandedPosts, setExpandedPosts] = useState([])
    const [isLoading, setIsLoading] = useState(false)

    const ShopID = accessibleShops?.length > 0 ? accessibleShops[0].ShopID : null
    const { faqPageBannerBackgroundColor, frontPageTextColorBlack, faqPageBannerImageUrl } = brandData?.features
    const textColor = frontPageTextColorBlack ? styleGuide.color18 : styleGuide.color1

    const bannerImageUrl = faqPageBannerImageUrl || 'https://cdn.upgrader.club/8a653249-819f-437d-8fc8-5c2584949e10-default.png'

    const fetchFaqPosts = async () => {
        if (!accessibleBlogs.length){
            return []
        }

        setIsLoading(true)

        const accessibleFaqBlogIds = accessibleBlogs
            .filter(item => item.blogType === 'FAQ')
            .map((item) => item.BlogID);

        const sortBy = "poolOrder";
        const sortOrder = "ASC";
        const faqItems = (await callAPI(
            "/post/getPostsOfMultipleBlogs",
            "GET",
            { query: { BlogIDs: accessibleFaqBlogIds, sortBy, sortOrder } }
        )) || [];

        if (faqItems && faqItems.length > 0) {
            const tempFaqCategories = []
            for (const faqItem of faqItems) {
                const category = faqItem.category || ''
                const faqCategory = tempFaqCategories.find(c => c.category === category)
                if (!faqCategory) {
                    tempFaqCategories.push({category: category, posts: [faqItem], poolOrder: faqItem.poolOrder})
                } else {
                    faqCategory.posts.push(faqItem)
                    faqCategory.poolOrder = Math.min(faqCategory.poolOrder, faqItem.poolOrder)
                }
            }
            tempFaqCategories.sort((a, b) => a.poolOrder - b.poolOrder)
            setFaqCategories(tempFaqCategories)
        }
        
        setIsLoading(false)
    }

    const textToHtmlWithClickableLinks = (htmlString) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');

        let textContent = doc.body.innerHTML;
        const newLineRegex = /(?:\r\n|\r|\n)/g;
        textContent = textContent.replace(newLineRegex, '<br>');

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = textContent;
        return tempDiv.innerHTML;
    }

    const parseFAQsContent = (post) => {
        const text = post?.translations?.find((translation) => translation.languageKey === langCode)?.elementContent || post.elementContent
        return textToHtmlWithClickableLinks(text)
    }

    const onExpandAccordionChange = (key, expanded) => {
        if(expanded){
            setExpandedPosts([...expandedPosts, key])
        } else {
            setExpandedPosts(expandedPosts.filter(k => k !== key))
        }
    }

    useEffect(() => {
        fetchFaqPosts()
    }, [accessibleBlogs])

    return (
        <Box display={'flex'} flexDirection={'column'} gap={4}>
            <BannerSection bannerImageUrl={bannerImageUrl} backgroundColor={faqPageBannerBackgroundColor || styleGuide.color3} backgroundHeightPct={80} textBackgroundColor={styleGuide.color14} headerSection>
                <Box flex={1} display={'flex'} justifyContent={{xs: 'left', md: 'center'}} padding={{xs: '20px 20px 40px 20px', sm: '32px 32px 48px 32px', lg: 6, xl: 10}}>
                    <Box display={'flex'} flexDirection={'column'} gap={4}>
                        <Headline3
                            translationKey="_FAQ_HEADLINE_"
                            fallbackText="FAQ"
                            style={{color: textColor}}
                        />

                        <Box display={'flex'} flexDirection={'column'} gap={1}>
                            <ContentNormal
                                translationKey="_FAQ_DESCRIPTION_1_"
                                fallbackText="Her finder du svar på de oftest stillede spørgsmål."
                                style={{color: textColor}}
                            />

                            <ContentNormal
                                translationKey="_FAQ_DESCRIPTION_2_"
                                fallbackText="Finder du ikke det du søger, kan du altid tage fat i os."
                                style={{color: textColor}}
                            />
                        </Box>

                        <Box display={'flex'} gap={2} marginTop={2} flexWrap={'wrap'}>
                            <ButtonMedium4
                                translationKey="_CONTACT_US_"
                                fallbackText="Kontakt os"
                                onClick={() => navigate('/contact')}
                                showIcon={false}
                                color={textColor}
                            />

                            {ShopID &&
                                <ButtonMedium4
                                    translationKey="_GO_TO_SHOP_BUTTON_TEXT_"
                                    fallbackText="Gå til shoppen"
                                    onClick={() => navigate(`/shop/${ShopID}`)}
                                    color={textColor}
                                />
                            }
                        </Box>
                    </Box>
                </Box>
            </BannerSection>
            <SectionWrapper>
                <Box borderRadius={'14px'} border={'1px solid #efefef'} padding={isMobile ? 3 : 6} marginTop={4}>
                    {isLoading
                        ? <Box display={'flex'} justifyContent={'center'}>
                            <CircularIndeterminate />
                        </Box>
                        : !!faqCategories?.length &&
                            <Box display={'flex'} flexDirection={'column'} gap={3}>
                                {faqCategories.map((cat, idx) => (
                                    <Box key={idx} display={'flex'} flexDirection={'column'} gap={3}>
                                        <Headline5>
                                            {cat.category || " "}
                                        </Headline5>

                                        <Box>
                                            {cat.posts.map(item => (
                                                <UpgraderAccordion key={item.elementTitle} onChange={(_, expanded) => onExpandAccordionChange(item.elementTitle, expanded)}>
                                                    <UpgraderAccordionSummary>
                                                        <ContentNormal style={{fontWeight: expandedPosts.some(k => k === item.elementTitle) ? 600 : 400}}>
                                                            {item?.translations?.find((translation) => translation.languageKey === langCode)?.elementTitle || item.elementTitle}
                                                        </ContentNormal>
                                                    </UpgraderAccordionSummary>
                                                    <UpgraderAccordionDetails>
                                                        <ContentNormal>
                                                            <div dangerouslySetInnerHTML={{ __html: parseFAQsContent(item) }}></div>
                                                        </ContentNormal>
                                                    </UpgraderAccordionDetails>
                                                </UpgraderAccordion>
                                            ))}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                    }
                </Box>
            </SectionWrapper>
        </Box>
    )
}
export default Help